/**
 * Admin model repoint — the shapes of what one batch moved and what it dropped.
 *
 * The repoint (`POST /api/admin/ai-models/migrate-resources`) moves every stored
 * reference to a retired model onto its replacement and re-judges each stored effort
 * level against the new model. These schemas are its response's row-level half.
 */

import { z } from "zod";

/** The models whose whole `modelConfig` column an admin repoint rewrites. */
export const RepointedModelConfigResourceSchema = z.enum(["agent", "aiTask", "agentTask"]);

export type RepointedModelConfigResource = z.infer<typeof RepointedModelConfigResourceSchema>;

/**
 * Everything an admin repoint moves: the three `modelConfig` columns, and a workflow
 * node's `data.modelOverride` inside `Workflow.nodes` or `Workflow.publishedNodes`.
 */
export const RepointedResourceSchema = z.enum([
  ...RepointedModelConfigResourceSchema.options,
  "workflowNode"
]);

export type RepointedResource = z.infer<typeof RepointedResourceSchema>;

/** The two node arrays a workflow stores: the draft graph and the published one. */
export const RepointedNodeArraySchema = z.enum(["nodes", "publishedNodes"]);

export type RepointedNodeArray = z.infer<typeof RepointedNodeArraySchema>;

const DroppedRepointLevelBaseSchema = z.object({
  /** The row's id — for a `workflowNode`, the WORKFLOW's. */
  id: z.string(),
  /** Null on a legacy row that predates organization ownership. */
  organizationId: z.string().nullable(),
  fromModelName: z.string(),
  toModelName: z.string(),
  reason: z.string()
});

/**
 * One repointed config whose stored effort level the new model refuses. The repoint
 * removes that config's level — the new model's default applies — and `reason` is the
 * effort-level policy's refusal, naming the field and the word.
 *
 * A `workflowNode` entry names the node inside its workflow: `nodeId` is the node's own
 * id and `nodeArray` the column it sits in, since the draft and the published graph
 * each hold their own copy of a node and each is judged on its own.
 */
export const DroppedRepointLevelSchema = z.discriminatedUnion("resource", [
  DroppedRepointLevelBaseSchema.extend({ resource: RepointedModelConfigResourceSchema }),
  DroppedRepointLevelBaseSchema.extend({
    resource: z.literal("workflowNode"),
    nodeId: z.string(),
    nodeArray: RepointedNodeArraySchema
  })
]);

export type DroppedRepointLevel = z.infer<typeof DroppedRepointLevelSchema>;
